// Everything a recruiter↔candidate thread needs in order to read the way it
// reads, with no JSX in it: the time and size formatters, the status and error
// vocabularies, and the three predicates the bubble branches on.
//
// Two surfaces render these threads — the candidate-scoped Communication
// Center at /admin/communications, and the application-scoped email tab on
// /admin/candidate-analysis — and they must read identically. A second copy of
// DELIVERY_ERROR_TEXT is the drift that ends with one screen explaining a
// bounce and the other printing `graph_send_rejected`.

import { AlertCircle, Ban, Check, CheckCheck, Clock, Mail, MessageSquare, Send } from "lucide-react";

// Mirrors settings.GRAPH_ATTACHMENT_MAX_SIZE_MB (graph_email_provider.py) —
// Microsoft Graph's simple attachment API is only reliable up to ~3MB, well
// under the general 15MB limit. Named here because DELIVERY_ERROR_TEXT below
// quotes it back to the recruiter.
export const GRAPH_ATTACHMENT_MAX_SIZE_MB = 3;

// The ceilings `SendMessageRequest` enforces (MESSAGE_SUBJECT_MAX /
// MESSAGE_BODY_MAX in server/app/api/v2/outreach/schema/api.py), read by every
// composer. One that let a recruiter type past them would hand back a 422
// after the whole message was written.
export const MESSAGE_SUBJECT_MAX = 500;
export const MESSAGE_BODY_MAX = 10000;

export const fmtBytes = (n) => {
  if (!n) return "0 KB";
  const kb = n / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

// 24-hour, everywhere a thread prints a clock. These messages go out on a
// send window expressed in UTC and are read by someone who is not in it, so a
// recruiter comparing a bubble against a scheduled send should not have to
// find the am/pm first — and at noon and midnight a 12-hour clock prints "12"
// for both. `hourCycle: "h23"` rather than `hour12: false`, which some
// engines resolve to h24 and print midnight as "24:05".
const CLOCK = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };

export const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], CLOCK);
};

export const fmtWhen = (iso) => {
  if (!iso) return "soon";
  const d = new Date(iso);
  return d.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...CLOCK,
  });
};

// The whole instant, with the zone named. A bubble's bare "14:05" sits under a
// day divider that already places it, so it stays bare and carries this in its
// `title`; anywhere an instant stands on its own — a table cell, a timeline
// event, the upcoming pill — this is what shows.
// How a mail client dates a row: the clock for today, the day for this year,
// the full date for anything older. The whole instant goes in a title beside
// it (fmtExactZoned).
export const fmtMailDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], CLOCK);
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
};

export const fmtExactZoned = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...CLOCK,
    timeZoneName: "short",
  });
};

export const dayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  // "Today" and "Yesterday" are relative to the reader's own clock already —
  // naming a zone beside them would answer a question nobody asked. An older
  // divider is the ambiguous one, so that is where the zone goes.
  if (d.toDateString() === now.toDateString()) return "Today";
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZoneName: "short",
  });
};

// One channel colour map, for every surface that shows a channel: the contact
// list's dot, the message table's column, the composer's chips. There were
// two of these — the Communication Center's and the old Message Log's — and
// they had already drifted into different greens.
//
// Tone tokens rather than raw palette classes, so a channel reads the same in
// both themes. Four forms, because four shapes need the colour: `text` for
// an icon or a word, `soft` for a chip, `fill` for the one solid control (the
// send button) and `onFill` for the label on that fill. `onFill` is the tone's
// own soft value — the pair tones.js measures at 4.5:1 or better in both
// themes. White on the dark theme's bright fill was about 1.5:1.
//
// `label` is the UI label; CHANNEL_DISPLAY_NAME above is the prose one
// ("send over email") and stays separate on purpose.
export const CHANNEL_TONE = {
  email: {
    label: "Email",
    Icon: Mail,
    text: "text-tone-blue",
    soft: "bg-tone-blue-soft text-tone-blue",
    fill: "bg-tone-blue",
    onFill: "text-tone-blue-soft",
  },
  whatsapp: {
    label: "WhatsApp",
    Icon: MessageSquare,
    text: "text-tone-emerald",
    soft: "bg-tone-emerald-soft text-tone-emerald",
    fill: "bg-tone-emerald",
    onFill: "text-tone-emerald-soft",
  },
  telegram: {
    label: "Telegram",
    Icon: Send,
    text: "text-tone-violet",
    soft: "bg-tone-violet-soft text-tone-violet",
    fill: "bg-tone-violet",
    onFill: "text-tone-violet-soft",
  },
};

// The three channels, in the order every filter offers them. One list, so a
// channel added to CHANNEL_TONE reaches the Chat view's tabs and the Table
// view's select without a second edit in either place.
export const CHANNEL_ORDER = ["whatsapp", "telegram", "email"];

// The server can add a channel before this bundle ships. Falling through to
// the email entry used to render a Mail icon over a Telegram message, which
// said the wrong thing confidently; a neutral tone says nothing instead.
export const channelTone = (channel) =>
  CHANNEL_TONE[channel] || {
    label: channel || "Unknown",
    Icon: Mail,
    text: "text-text-secondary",
    soft: "bg-tone-slate-soft text-tone-slate",
    fill: "bg-tone-slate",
    onFill: "text-tone-slate-soft",
  };

// One sentiment colour map, for every surface that shows what a candidate
// said: the message bubble's chip, the communications timeline, and the
// sequence detail page's filter badges. `soft` is the fill-and-text pair every
// chip wants; `border` is the outline only the filter badge draws.
//
// Both halves are written out rather than composed from the tone name, because
// Tailwind scans for whole class names — `bg-tone-${tone}-soft` compiles to
// nothing.
export const SENTIMENT_TONE = {
  interested: { soft: "bg-tone-emerald-soft text-tone-emerald", border: "border-tone-emerald/30" },
  not_interested: { soft: "bg-tone-rose-soft text-tone-rose", border: "border-tone-rose/30" },
  has_questions: { soft: "bg-tone-blue-soft text-tone-blue", border: "border-tone-blue/30" },
  out_of_office: { soft: "bg-tone-amber-soft text-tone-amber", border: "border-tone-amber/30" },
  unsubscribe: { soft: "bg-tone-rose-soft text-tone-rose", border: "border-tone-rose/30" },
  wrong_person: { soft: "bg-tone-slate-soft text-tone-slate", border: "border-tone-slate/30" },
  other: { soft: "bg-tone-slate-soft text-tone-slate", border: "border-tone-slate/30" },
};

// The server can add a sentiment before this bundle ships, so every reader
// falls through to `other` rather than rendering an unstyled chip.
export const sentimentTone = (sentiment) => SENTIMENT_TONE[sentiment] || SENTIMENT_TONE.other;

export const SENTIMENT_COLORS = Object.fromEntries(Object.entries(SENTIMENT_TONE).map(([name, tone]) => [name, tone.soft]));

export const SENTIMENT_FALLBACK_COLOR = SENTIMENT_TONE.other.soft;

// The outbound bubble is `bg-button-secondary` with `text-text-base` on it, in
// both themes, so each badge is stated as an alpha of that label token rather
// than against the page surface. The tone tokens mark the states that need a
// colour of their own: amber for waiting, rose for stopped.
export const OUTBOUND_STATUS = {
  queued: { icon: Clock, badgeCls: "border-text-base/20 bg-text-base/10 text-text-base/80", label: "Queued" },
  scheduled: { icon: Clock, badgeCls: "border-tone-amber/40 bg-tone-amber/15 text-text-base", label: "Scheduled" },
  sending: { icon: Clock, badgeCls: "border-text-base/20 bg-text-base/10 text-text-base/80", label: "Sending" },
  sent: { icon: Check, badgeCls: "border-text-base/30 bg-text-base/15 text-text-base", label: "Sent" },
  delivered: { icon: CheckCheck, badgeCls: "border-text-base/35 bg-text-base/20 text-text-base", label: "Delivered" },
  opened: { icon: CheckCheck, badgeCls: "border-text-base/50 bg-text-base/25 text-text-base", label: "Read" },
  clicked: { icon: CheckCheck, badgeCls: "border-text-base/50 bg-text-base/25 text-text-base", label: "Read" },
  replied: { icon: CheckCheck, badgeCls: "border-text-base/50 bg-text-base/25 text-text-base", label: "Replied" },
  bounced: { icon: AlertCircle, badgeCls: "border-tone-rose/45 bg-tone-rose/35 text-text-base", label: "Bounced" },
  failed: { icon: AlertCircle, badgeCls: "border-tone-rose/45 bg-tone-rose/35 text-text-base", label: "Failed" },
  unsubscribed: { icon: Ban, badgeCls: "border-tone-rose/45 bg-tone-rose/35 text-text-base", label: "Unsubscribed" },
  skipped: { icon: Ban, badgeCls: "border-text-base/20 bg-tone-slate/20 text-text-base/90", label: "Skipped" },
};

// Provider error codes are stable identifiers, not sentences. Translate the
// ones we raise ourselves into plain language; anything else (e.g. Meta's own
// wording, which is already a readable sentence) is shown as-is.
export const DELIVERY_ERROR_TEXT = {
  no_phone: "No phone number on this candidate's profile.",
  multiple_numbers: "The candidate's phone field contains more than one number.",
  has_extension: "The candidate's phone number has an extension or extra digits.",
  too_short: "The phone number has too few digits.",
  too_long: "The phone number has too many digits.",
  not_a_mobile: "That's a landline. WhatsApp needs a mobile number.",
  invalid_for_region: "Not a valid mobile number for its country.",
  unparseable: "The phone number could not be understood.",
  not_on_whatsapp: "This number isn't registered on WhatsApp.",
  no_template_configured: "No approved WhatsApp template is configured for cold outreach.",
  whatsapp_provider_not_configured: "WhatsApp (WATI) isn't configured in this environment.",
  telegram_not_linked: "This candidate hasn't started a chat with the Telegram bot yet — send them the Telegram invite link first.",
  telegram_awaiting_link: "Waiting for the candidate to join Telegram — the invite was emailed to them.",
  telegram_provider_not_configured: "Telegram isn't connected for this org yet — connect it under Org Settings.",
  blocked_by_user: "The candidate has blocked this bot on Telegram.",
  email_provider_not_configured: "The email provider isn't configured in this environment.",
  email_disabled: "Email sending is disabled.",
  address_suppressed: "This address has unsubscribed or bounced previously.",
  // Microsoft Graph (active email provider) — see graph_email_provider.py's
  // _ERROR_CODE_MAP for where these codes come from.
  graph_auth_failed: "Couldn't authenticate with Microsoft Graph — check the outreach mailbox connection.",
  graph_permission_denied: "Microsoft Graph denied this request — the outreach mailbox may need admin re-consent.",
  graph_not_found: "Microsoft Graph couldn't find the outreach mailbox.",
  graph_throttled: "Microsoft Graph is rate-limiting outreach email right now — this will retry automatically.",
  graph_server_error: "Microsoft Graph had a server error — this will retry automatically.",
  graph_timeout: "The request to Microsoft Graph timed out — this will retry automatically.",
  graph_send_rejected: "Microsoft Graph rejected this email — check the recipient address.",
  attachment_too_large: `This file is larger than the ${GRAPH_ATTACHMENT_MAX_SIZE_MB}MB limit for email attachments. Try a smaller file.`,
};

// last_error is stored as "code: human detail" by the sender, or as a bare
// provider sentence by the delivery webhook. Render whichever is most readable.
export const formatDeliveryError = (lastError) => {
  const raw = String(lastError || "").trim();
  if (!raw) return "";
  const [, code, rest] = raw.match(/^([a-z0-9_]+):\s*([\s\S]+)$/i) || [];
  if (code && rest) return rest.trim();
  return DELIVERY_ERROR_TEXT[raw] || raw;
};

// Telegram's Bot API has no delivery/read-receipt concept at all — "Sent" is
// the permanent ceiling for a Telegram message unless the candidate replies
// (-> Replied) or blocks the bot (-> Failed). Without this note the tick looks
// stuck/broken instead of accurately reflecting a platform limitation no
// amount of backend work can lift.
export const TELEGRAM_SENT_NOTE = "Telegram doesn't provide delivery or read receipts to bots — this is the final status unless the candidate replies.";

// The code a Telegram sequence step carries while it waits for an unlinked
// candidate to press Start in the emailed invite (sender.TELEGRAM_AWAITING_LINK).
export const TELEGRAM_AWAITING_LINK = "telegram_awaiting_link";

export const CHANNEL_DISPLAY_NAME = { whatsapp: "WhatsApp", telegram: "Telegram", email: "email" };

export const isUpcomingItem = (item) => item.direction === "outbound" && !item.sent_at && ["scheduled", "queued", "sending"].includes(item.status);

// WhatsApp/Telegram only — quoting has no native equivalent on email, and the
// backend rejects it outright for that channel.
export const canQuoteChannel = (channel) => channel === "whatsapp" || channel === "telegram";

// Inbound-only auto-generated label the backend stores when a WhatsApp/Telegram
// media reply had no real caption (e.g. "[photo]", "[document]") — useful as
// the sidebar's last-message preview, but redundant under the real thumbnail
// or file chip it now renders alongside.
export const AUTO_MEDIA_PLACEHOLDER_RE = /^\[(photo|image|document|video|audio|voice|sticker|message)\]$/i;

// What a quoted message's snippet shows: its own text, or — if it had no text
// (an attachment sent with no caption) — an attachment hint instead of a
// blank/"(no content)" line.
export const quotedPreviewText = (quoted) => {
  const text = (quoted?.text || "").trim();
  if (text) return text;
  if (quoted?.attachment) {
    return quoted.attachment.is_image ? "📷 Photo" : `📄 ${quoted.attachment.filename || "Document"}`;
  }
  return "(no content)";
};

// Where a candidate's own words end and the history their mail client quoted
// under them begins. The stored text stays whole; the reading pane folds
// `quoted` away the way Gmail does. A cut that leaves nothing above it is not
// a cut — the whole text is the message.
const QUOTE_ATTRIBUTION_REACH = 4; // lines an "On … wrote:" may wrap across
const isAttributionStart = (lines, i) => {
  if (!/^On\s/.test(lines[i].trim())) return false;
  const reach = lines.slice(i, i + QUOTE_ATTRIBUTION_REACH);
  return reach.some((line) => /wrote:\s*$/.test(line.replace(/^>\s?/, "").trim()));
};
const isOutlookHeader = (lines, i) =>
  /^From:\s/.test(lines[i].trim()) && lines.slice(i + 1, i + 5).some((line) => /^(Sent|Date):\s/.test(line.trim()));
const isQuoteStart = (lines, i) =>
  lines[i].trim().startsWith(">") ||
  /^-{2,}\s*Original Message\s*-{2,}$/i.test(lines[i].trim()) ||
  isAttributionStart(lines, i) ||
  isOutlookHeader(lines, i);

export const splitQuotedEmail = (text) => {
  const whole = text || "";
  const lines = whole.split("\n");
  let cut = lines.findIndex((_, i) => isQuoteStart(lines, i));
  if (cut === -1) return { body: whole, quoted: "" };
  // Outlook rules the history off with a line of underscores.
  while (cut > 0 && /^_{5,}$/.test(lines[cut - 1].trim())) cut -= 1;
  const body = lines.slice(0, cut).join("\n").trimEnd();
  if (!body.trim()) return { body: whole, quoted: "" };
  return { body, quoted: lines.slice(cut).join("\n").trim() };
};
